import {
  faAngleDown,
  faAngleUp,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { cva, VariantProps } from "class-variance-authority";
import {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  components,
  ContainerProps,
  DropdownIndicatorProps,
  MenuProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import {
  ActionMeta,
  GroupBase,
  MenuPlacement,
  OptionsOrGroups,
  SetValueAction,
} from "react-select/dist/declarations/src/types";
import { AsyncPaginate } from "react-select-async-paginate";
import { useOnClickOutside } from "usehooks-ts";

import CogIcon from "../../../assets/img/cog.svg";
import { Badge } from "./Badge";
import { Spinner } from "./Spinner";

export interface PaginatedSelectOption {
  value: any;
  label: ReactNode;
  isDisabled?: boolean;
}

export interface PaginatedSelectAdditional {
  page: number;
}

const paginatedSelectVariants = cva("", {
  variants: {
    size: {
      small: "h-[2.375rem] text-sm",
      medium: "",
    },
    color: {
      default: "border-gray-300 dark:!border-gray-700",
      accent: "border-secondary-500 bg-secondary-500 hover:bg-secondary-500",
    },
  },
  defaultVariants: {
    size: "medium",
    color: "default",
  },
});

export type PaginatedSelectVariantProps = VariantProps<
  typeof paginatedSelectVariants
>;

interface LoadOptionsResult {
  readonly options: PaginatedSelectOption[];
  readonly hasMore?: boolean;
  readonly additional?: PaginatedSelectAdditional;
}

export interface SelectAllClickArgs {
  isAllSelected: boolean;
  fetchAllOptions: (searchTerm?: string) => Promise<{
    allOptions: PaginatedSelectOption[];
    pageCount: number;
  }>;
  setValue: (newValue: any, action: SetValueAction, option?: any) => void;
}

export interface PaginatedSelectProps extends PaginatedSelectVariantProps {
  value: any;
  onChange: (newValue: any, action: ActionMeta<any>) => void;
  className?: string;
  loadOptions: (
    inputValue: string,
    loadedOptions: readonly PaginatedSelectOption[],
    additional: PaginatedSelectAdditional,
  ) => Promise<LoadOptionsResult>;
  isSearchable?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  cacheUniqs?: ReadonlyArray<any>;
  isDisabled?: boolean;
  isClearable?: boolean;
  icon?: ReactNode;
  compact?: boolean;
  menuWidthShouldMatchText?: boolean;
  showSelectAll?: boolean;
  selectAllClick?: (params: SelectAllClickArgs) => Promise<boolean | void>;
  showMenuFooterButton?: boolean;
  menuFooterText?: ReactNode;
  menuFooterClick?: () => Promise<void>;
  refetchOnMenuOpen?: boolean;
  badgeClearIndicator?: boolean;
  closeMenuOnSelect?: boolean;
  blurInputOnSelect?: boolean;
  menuPlacement?: MenuPlacement;
  debounceTimeout?: number;
  menuPortalTargetDocumentBody?: boolean;
  maxMenuHeight?: number;
  valueContainerClassName?: string;
}

export function PaginatedSelect({
  value,
  onChange: onChangeParam,
  className,
  loadOptions: loadOptionsParam,
  isSearchable = false,
  isMulti = false,
  placeholder,
  debounceTimeout,
  cacheUniqs: cacheUniqsParam,
  isDisabled = false,
  isClearable,
  icon,
  size,
  color,
  compact,
  menuWidthShouldMatchText,
  showSelectAll,
  selectAllClick: selectAllClickParam,
  showMenuFooterButton,
  menuFooterText,
  menuFooterClick,
  refetchOnMenuOpen,
  badgeClearIndicator,
  closeMenuOnSelect,
  blurInputOnSelect,
  menuPlacement,
  menuPortalTargetDocumentBody = false,
  maxMenuHeight,
  valueContainerClassName,
}: PaginatedSelectProps) {
  const optionRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasAlreadyFocusedValue = useRef(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  useEffect(() => {
    if (!isMenuOpen) {
      hasAlreadyFocusedValue.current = false;
    }
  }, [isMenuOpen]);

  const loadOptions = useCallback(
    async (
      inputValue: string,
      options: OptionsOrGroups<any, GroupBase<any>>,
      additional:
        | {
            page: number;
          }
        | undefined,
    ) => {
      const result = await loadOptionsParam(
        inputValue,
        options,
        additional || { page: 0 },
      );

      return {
        additional: { page: 0 },
        ...result,
      };
    },
    [loadOptionsParam],
  );

  const onChange = useCallback(
    (newValue: any, action: ActionMeta<any>) => {
      onChangeParam(newValue, action);
      if (isMulti && showSelectAll && isAllSelected) {
        setIsAllSelected(false);
      }
    },
    [isAllSelected, isMulti, onChangeParam, showSelectAll],
  );

  const cacheUniqs = useMemo(() => {
    let deps;
    if (refetchOnMenuOpen) {
      deps = [cacheUniqsParam, isMenuOpen];
    } else if (cacheUniqsParam) {
      deps = cacheUniqsParam;
    }

    return deps;
  }, [cacheUniqsParam, isMenuOpen, refetchOnMenuOpen]);

  const ref = useRef(null);

  useOnClickOutside(ref, () => {
    if (isMulti) {
      setIsMenuOpen(false);
    }
  });

  const customMenu: ComponentType<MenuProps<any, boolean, GroupBase<any>>> =
    useCallback(({ children, ...props }) => {
      const { menuFooterClick, menuFooterText, showMenuFooterButton, size } = (
        props.selectProps as any
      ).customProps;
      return (
        <components.Menu {...props}>
          <SelectHeader {...props} />
          {children}
          <div
            className={cn(
              optionVariants({ size }),
              "flex gap-2 border-t border-t-gray-200 px-3 py-3",
              { hidden: !showMenuFooterButton },
            )}
            onClick={() => {
              if (menuFooterClick) {
                menuFooterClick();
              }
              setIsMenuOpen(false);
            }}
          >
            <img alt="" src={CogIcon} />
            {menuFooterText ?? "Manage"}
          </div>
        </components.Menu>
      );
    }, []);
  const customValueContainer: ComponentType<
    ValueContainerProps<any, boolean, GroupBase<any>>
  > = useCallback(
    ({ children, className, ...props }) => {
      return (
        <>
          <div className={cn("pl-2", { hidden: !icon })}>{icon}</div>
          <components.ValueContainer
            className={cn(
              "!py-0 !pl-0 text-left font-semibold",
              valueContainerVariants({ color }),
              { "!pr-4": !compact },
              { "ml-4": !icon },
              className,
              valueContainerClassName,
            )}
            {...props}
          >
            {children}
          </components.ValueContainer>
        </>
      );
    },
    [color, compact, icon, valueContainerClassName],
  );

  const customOption: ComponentType<OptionProps<any, boolean, GroupBase<any>>> =
    useCallback(
      ({ children, ...props }) => {
        if (!hasAlreadyFocusedValue.current) {
          hasAlreadyFocusedValue.current = true;
          setTimeout(() => {
            if (optionRef.current) {
              optionRef.current?.parentElement?.scroll({
                top: optionRef.current.offsetTop,
              });
            }
          }, 10);
        }
        const isMulti = props.selectProps.isMulti;
        const value = props.selectProps.value;
        return (
          <div
            ref={(props as any).value === value?.value ? optionRef : undefined}
          >
            <components.Option {...props}>
              <div
                className={cn("flex items-center gap-2", {
                  "text-gray-200": props.isDisabled,
                })}
              >
                <div
                  className={cn("flex h-6 items-center", {
                    hidden: !isMulti,
                  })}
                >
                  <input
                    checked={props.isSelected || isAllSelected}
                    type="checkbox"
                    className="text-secondary-600 size-4 border-gray-300"
                    onChange={() => {}}
                  />
                </div>
                <div className="flex-1">{children}</div>
              </div>
            </components.Option>
          </div>
        );
      },
      [isAllSelected],
    );

  const customDropdownIndicator: ComponentType<
    DropdownIndicatorProps<any, boolean, GroupBase<any>>
  > = useCallback(
    ({ selectProps: { menuIsOpen } }: any) => {
      if (compact) {
        return null;
      }
      if (menuIsOpen) {
        return (
          <FontAwesomeIcon
            icon={faAngleUp}
            className={cn(dropdownIndicatorVariants({ color, size }))}
          />
        );
      }
      return (
        <FontAwesomeIcon
          icon={faAngleDown}
          className={cn(dropdownIndicatorVariants({ color, size }))}
        />
      );
    },
    [color, compact, size],
  );

  const customSelectContainer: ComponentType<
    ContainerProps<any, boolean, GroupBase<any>>
  > = useCallback(({ children, ...props }) => {
    return (
      <components.SelectContainer {...props}>
        <div className="w-100" ref={ref}>
          {children}
        </div>
      </components.SelectContainer>
    );
  }, []);

  const customClearIndicator = useCallback(
    ({ children, ...props }: any) => {
      if (isMulti && badgeClearIndicator) {
        const value = props.getValue();
        return (
          <Badge
            className="mr-2 flex items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div>{value.length}</div>
            <FontAwesomeIcon
              className="cursor-pointer text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                props.clearValue();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              icon={faClose}
            />
          </Badge>
        );
      }

      return (
        <components.ClearIndicator {...props}>
          {children}
        </components.ClearIndicator>
      );
    },
    [badgeClearIndicator, isMulti],
  );

  return (
    <AsyncPaginate
      loadOptions={loadOptions}
      components={{
        SelectContainer: customSelectContainer,
        DropdownIndicator: customDropdownIndicator,
        IndicatorSeparator,
        Option: customOption,
        ValueContainer: customValueContainer,
        Menu: customMenu,
        ClearIndicator: customClearIndicator,
      }}
      classNames={{
        container: ({ isFocused, className }) =>
          cn(className, {
            "border-secondary-600": isFocused && isMenuOpen,
          }),
        control: () =>
          "!border-none bg-transparent !outline-0 !shadow-none !flex !flex-row justify-between w-full !flex-nowrap min-h-0",
        menu: () =>
          cn("!z-[9999] !rounded-lg dark:bg-gray-950 overflow-hidden", {
            "-ml-[4.2rem]": compact,
            "w-auto": compact || menuWidthShouldMatchText,
          }),
        menuPortal: () => "!z-[99999]",
        option: ({ isSelected }: { isSelected: boolean }) =>
          cn(optionVariants({ size }), {
            "font-semibold": isSelected,
          }),
        valueContainer: () =>
          cn("border-none shadow-none cursor-pointer", {
            "!ml-2": compact,
          }),
      }}
      className={cn(
        "dark:hover:bg-gray-900 relative flex min-w-fit cursor-pointer items-center !rounded-lg border border-gray-300 text-center hover:bg-gray-100",
        paginatedSelectVariants({ size, color }),
        { "bg-gray-100": isDisabled },
        {
          "after:bg-secondary-25 after:border-secondary-200 relative after:absolute after:right-0 after:top-0 after:flex after:aspect-square after:h-6 after:-translate-y-1/2 after:translate-x-1/2 after:items-center after:justify-center after:rounded-full after:border after:p-1 after:text-xs after:font-medium":
            isMulti && !badgeClearIndicator && value?.length > 0,
        },
        className,
      )}
      styles={{
        container: () => ({
          "&:after":
            isMulti && !badgeClearIndicator && value?.length > 0
              ? { content: `"${value?.length ?? 0}"` }
              : {},
        }),
      }}
      value={value}
      onChange={onChange}
      isSearchable={isSearchable}
      isMulti={isMulti}
      placeholder={placeholder}
      additional={{ page: 0 }}
      cacheUniqs={cacheUniqs}
      isDisabled={isDisabled}
      isClearable={isClearable}
      menuPortalTarget={
        menuPortalTargetDocumentBody ? document.body : undefined
      }
      menuPlacement={menuPlacement ?? "auto"}
      menuIsOpen={isMenuOpen}
      debounceTimeout={debounceTimeout}
      maxMenuHeight={maxMenuHeight}
      onMenuOpen={() => {
        // Fix for menu instantly closing with touch events
        setTimeout(() => {
          setIsMenuOpen(true);
        }, 1);
      }}
      onMenuClose={() => {
        setIsMenuOpen(false);
      }}
      closeMenuOnSelect={
        closeMenuOnSelect === undefined ? !isMulti : closeMenuOnSelect
      }
      blurInputOnSelect={
        blurInputOnSelect === undefined ? !isMulti : blurInputOnSelect
      }
      hideSelectedOptions={false}
      controlShouldRenderValue={!isMulti}
      {...{
        customProps: {
          isAllSelected,
          menuFooterClick,
          menuFooterText,
          selectAllClickParam,
          showMenuFooterButton,
          showSelectAll,
          setIsAllSelected,
          size,
        },
      }}
    />
  );
}

const SelectHeader = function SelectHeader({
  ...props
}: Omit<MenuProps<any, boolean, GroupBase<any>>, "children">) {
  const {
    isAllSelected,
    selectAllClickParam,
    setIsAllSelected,
    showSelectAll,
    size,
  } = (props.selectProps as any).customProps;
  const loadOptions = ((props as any).selectProps as any).loadOptions;
  const isMulti = (props as any).selectProps.isMulti;
  const [isLoading, setIsLoading] = useState(false);
  return (
    <div
      className={cn(
        optionVariants({ size }),
        "flex items-center gap-2 border-b border-b-gray-200 p-3",
        { hidden: !isMulti || !showSelectAll },
      )}
      onClick={async () => {
        if (!selectAllClickParam) {
          return;
        }
        const fetchAllOptions = async (searchTerm?: string) => {
          let allOptions: PaginatedSelectOption[] = [];
          let allLoaded = false;
          let page = 0;

          do {
            const result = await loadOptions(searchTerm || "", [], {
              page: page++,
            });
            allLoaded = !result.hasMore;
            allOptions = allOptions.concat(result.options);
          } while (!allLoaded);
          return { allOptions, pageCount: page };
        };
        setIsLoading(true);
        const result = await selectAllClickParam({
          isAllSelected,
          fetchAllOptions,
          setValue: (props as any).setValue,
        });
        if (result === undefined) {
          setIsAllSelected(!isAllSelected);
        } else {
          setIsAllSelected(result);
        }
        setIsLoading(false);
      }}
    >
      {isLoading && <Spinner className="!h-6 !w-6" />}
      {!isLoading && (
        <>
          <div className="flex h-6 items-center">
            <input
              checked={isAllSelected}
              type="checkbox"
              className="text-secondary-600 size-4 border-gray-300"
              onChange={() => {}}
            />
          </div>
          <div>{isAllSelected ? "Deselect All" : "Select All"}</div>
        </>
      )}
    </div>
  );
};

const dropdownIndicatorVariants = cva("mr-4 cursor-pointer", {
  variants: {
    color: {
      default: "text-sm font-semibold text-gray-700 dark:text-gray-50",
      accent: "text-gray-0",
    },
    size: {
      small: "mt-px",
      medium: "",
    },
  },
  defaultVariants: {
    color: "default",
    size: "medium",
  },
});

const valueContainerVariants = cva("", {
  variants: {
    color: {
      default:
        "text-sm font-semibold text-gray-700 dark:text-gray-50 [&>div]:text-gray-700 dark:[&>div]:text-gray-50",
      accent: "text-gray-0 [&>div]:text-gray-0",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

const optionVariants = cva(
  "!bg-gray-0 !cursor-pointer text-left !text-sm font-medium !text-gray-700 hover:!bg-gray-50 dark:!bg-gray-950 dark:!text-gray-50 dark:hover:!bg-gray-900",
  {
    variants: {
      size: {
        small: "",
        medium: "",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  },
);

function IndicatorSeparator() {
  return null;
}

export async function eagerSelectAll({
  fetchAllOptions,
  setValue,
  isAllSelected,
}: SelectAllClickArgs) {
  if (isAllSelected) {
    setValue([], "deselect-option");
    return;
  }
  const { allOptions } = await fetchAllOptions();
  setValue(allOptions, "select-option");
}

export function renderMultiSelectPlaceholder({
  value,
  itemLabel = "Selected",
  noValuePlaceholder,
}: {
  value?: { label: string }[];
  itemLabel?: string;
  noValuePlaceholder?: string;
}) {
  if (value === undefined || value?.length === 0) {
    return noValuePlaceholder;
  }
  if (value?.length === 1 && value?.[0]?.label) {
    return value[0].label;
  }
  if (value?.length > 1) {
    return `${value.length} ${itemLabel}`;
  }
  return noValuePlaceholder;
}
